import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { HC } from "../hr/theme";
import { Btn, Pill } from "../hr/ui";
import { DataTable, num, dt } from "./crmui";
import { toast } from "sonner";
import { Plus, Send, CheckCircle2, Users } from "lucide-react";

const TYPES = ["welcome", "post_purchase", "re_engagement", "loyalty", "promotional", "location", "product"];
const CHANNELS = ["email", "sms", "push", "whatsapp"];

export default function Campaigns() {
  const [rows, setRows] = useState([]);
  const [segments, setSegments] = useState([]);
  const [edit, setEdit] = useState(null);
  const [audience, setAudience] = useState(null);
  const [members, setMembers] = useState(null);
  const [connected, setConnected] = useState(false);

  const load = useCallback(() => {
    api.get("/crm/campaigns").then(setRows).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => {
    load();
    api.get("/crm/segments").then(setSegments).catch(() => {});
    api.get("/crm/settings").then((s) => setConnected(Object.values(s.integrations || {}).some((v) => v?.enabled))).catch(() => {});
  }, [load]);

  const save = async () => {
    if (!edit.name) { toast.error("Campaign name is required"); return; }
    try {
      if (edit.id) await api.put(`/crm/campaigns/${edit.id}`, edit);
      else await api.post("/crm/campaigns", edit);
      toast.success("Campaign saved"); setEdit(null); setAudience(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const act = async (fn, msg) => {
    try { const r = await fn(); toast.success(typeof msg === "function" ? msg(r) : msg); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="crm-campaigns-page">
      {!connected && (
        <div className="rounded-xl p-3 text-[12.5px]" style={{ background: HC.amberTint, color: HC.amber }} data-testid="campaign-integration-warning">
          No marketing platform is connected. Campaigns resolve their audience, respect consent and queue communications,
          but nothing is sent until CleverTap or Google Analytics is configured in CRM Settings.
        </div>
      )}
      <div className="flex justify-end">
        <Btn onClick={() => setEdit({ type: "promotional", channel: "email", content: { subject: "", body: "" }, schedule: { mode: "now" } })}
          data-testid="campaign-new"><Plus size={14} /> New Campaign</Btn>
      </div>

      <DataTable testid="crm-campaigns-table" rows={rows}
        columns={[["name", "Campaign"], ["type", "Type"], ["segment_name", "Audience"], ["channel", "Channel"],
                  ["status", "Status"], ["audience_count", "Audience"], ["created_by", "Created by"],
                  ["created_at", "Created"], ["actions", "Actions"]]}
        render={{
          status: (r) => <Pill status={r.status === "sent" ? "approved" : r.status === "draft" ? "draft" : "pending"} text={r.status} />,
          audience_count: (r) => num(r.audience_count),
          created_at: (r) => dt(r.created_at),
          actions: (r) => (
            <span className="flex gap-1.5">
              <button data-testid={`campaign-audience-${r.id.slice(0, 4)}`} className="h-7 px-2 rounded-md border text-[11.5px]" style={{ borderColor: HC.border }}
                onClick={async (e) => { e.stopPropagation(); setAudience(await api.get(`/crm/campaigns/${r.id}/audience`)); }}><Users size={12} /></button>
              {r.status === "draft" && (
                <button data-testid={`campaign-approve-${r.id.slice(0, 4)}`} className="h-7 px-2 rounded-md border text-[11.5px]" style={{ borderColor: HC.border }}
                  onClick={(e) => { e.stopPropagation(); act(() => api.post(`/crm/campaigns/${r.id}/approve`, {}), "Campaign approved"); }}><CheckCircle2 size={12} /></button>
              )}
              {["draft", "approved", "scheduled"].includes(r.status) && (
                <button data-testid={`campaign-dispatch-${r.id.slice(0, 4)}`} className="h-7 px-2 rounded-md border text-[11.5px]" style={{ borderColor: HC.border }}
                  onClick={(e) => { e.stopPropagation(); act(() => api.post(`/crm/campaigns/${r.id}/dispatch`, {}),
                    (res) => `${res.audience} customers queued${res.integration_connected ? "" : " — awaiting integration"}${res.skipped ? `, ${res.skipped} skipped (no consent)` : ""}`); }}><Send size={12} /></button>
              )}
              <button data-testid={`campaign-members-${r.id.slice(0, 4)}`} className="h-7 px-2 rounded-md border text-[11.5px]" style={{ borderColor: HC.border }}
                onClick={async (e) => { e.stopPropagation(); setMembers(await api.get(`/crm/campaigns/${r.id}/members`)); }}>List</button>
            </span>
          ),
        }}
        onRowClick={(r) => setEdit({ ...r })} empty="No campaigns yet" />

      {edit && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: "rgba(51,34,30,0.45)" }}>
          <div className="bg-white rounded-2xl border w-full max-w-lg p-5 space-y-3 my-8" style={{ borderColor: HC.border }} data-testid="campaign-modal">
            <h3 className="font-brand font-bold text-lg" style={{ color: HC.text }}>{edit.id ? "Edit campaign" : "New campaign"}</h3>
            <input placeholder="Campaign name *" value={edit.name || ""} data-testid="campaign-name"
              onChange={(e) => setEdit({ ...edit, name: e.target.value })}
              className="h-10 px-3 w-full rounded-md border text-[13px]" style={{ borderColor: HC.border }} />
            <input placeholder="Description" value={edit.description || ""} data-testid="campaign-description"
              onChange={(e) => setEdit({ ...edit, description: e.target.value })}
              className="h-10 px-3 w-full rounded-md border text-[13px]" style={{ borderColor: HC.border }} />
            <div className="grid grid-cols-2 gap-2">
              <select value={edit.type} onChange={(e) => setEdit({ ...edit, type: e.target.value })} data-testid="campaign-type"
                className="h-10 px-2 rounded-md border text-[12.5px]" style={{ borderColor: HC.border }}>
                {TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </select>
              <select value={edit.channel} onChange={(e) => setEdit({ ...edit, channel: e.target.value })} data-testid="campaign-channel"
                className="h-10 px-2 rounded-md border text-[12.5px]" style={{ borderColor: HC.border }}>
                {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <select value={edit.segment_id || ""} onChange={(e) => setEdit({ ...edit, segment_id: e.target.value || null })}
              data-testid="campaign-segment" className="h-10 px-2 w-full rounded-md border text-[12.5px]" style={{ borderColor: HC.border }}>
              <option value="">All customers</option>
              {segments.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.customer_count})</option>)}
            </select>
            <input placeholder="Subject" value={edit.content?.subject || ""} data-testid="campaign-subject"
              onChange={(e) => setEdit({ ...edit, content: { ...edit.content, subject: e.target.value } })}
              className="h-10 px-3 w-full rounded-md border text-[13px]" style={{ borderColor: HC.border }} />
            <textarea placeholder="Message body" value={edit.content?.body || ""} data-testid="campaign-body" rows={4}
              onChange={(e) => setEdit({ ...edit, content: { ...edit.content, body: e.target.value } })}
              className="px-3 py-2 w-full rounded-md border text-[13px]" style={{ borderColor: HC.border }} />
            <div className="flex justify-end gap-2">
              <Btn variant="outline" onClick={() => setEdit(null)}>Cancel</Btn>
              <Btn onClick={save} data-testid="campaign-save">Save</Btn>
            </div>
          </div>
        </div>
      )}

      {audience && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: "rgba(51,34,30,0.45)" }}>
          <div className="bg-white rounded-2xl border w-full max-w-2xl p-5 space-y-3 my-8" style={{ borderColor: HC.border }} data-testid="campaign-audience-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-brand font-bold text-lg" style={{ color: HC.text }}>
                Audience — {audience.eligible} eligible, {audience.skipped_no_consent} skipped (no consent)
              </h3>
              <Btn variant="outline" onClick={() => setAudience(null)}>Close</Btn>
            </div>
            <DataTable testid="campaign-audience-table" rows={audience.sample}
              columns={[["name", "Customer"], ["phone", "Phone"], ["email", "Email"], ["loyalty_tier", "Tier"], ["marketing_consent", "Consent"]]}
              render={{ marketing_consent: (r) => <Pill status={r.marketing_consent ? "approved" : "rejected"} text={r.marketing_consent ? "Yes" : "No"} /> }}
              empty="No eligible customers" />
          </div>
        </div>
      )}

      {members && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: "rgba(51,34,30,0.45)" }}>
          <div className="bg-white rounded-2xl border w-full max-w-2xl p-5 space-y-3 my-8" style={{ borderColor: HC.border }} data-testid="campaign-members-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-brand font-bold text-lg" style={{ color: HC.text }}>Campaign membership ({members.total})</h3>
              <Btn variant="outline" onClick={() => setMembers(null)}>Close</Btn>
            </div>
            <DataTable testid="campaign-members-table" rows={members.rows}
              columns={[["customer_name", "Customer"], ["status", "Status"], ["reason", "Reason"], ["at", "When"]]}
              render={{ at: (r) => dt(r.at) }} empty="No memberships yet" />
          </div>
        </div>
      )}
    </div>
  );
}
